import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, ArrowRight, Sparkles, AlertCircle 
} from "lucide-react";
import BrandHeader from "../../components/BrandHeader";
import OtpLoginModal from "../../components/auth/OtpLoginModal";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);

  const redirectUrl = searchParams.get("redirect");

  const redirectBasedOnRole = (userData) => {
    if (userData?.is_admin || userData?.is_superuser || userData?.role === "admin") {
      navigate("/admin", { replace: true });
    } else if (redirectUrl) {
      const isSafeRedirect = redirectUrl.startsWith("/") && !redirectUrl.startsWith("//") && !redirectUrl.includes("://");
      navigate(isSafeRedirect ? redirectUrl : "/leads", { replace: true });
    } else {
      navigate("/leads", { replace: true });
    }
  };

  useEffect(() => {
    if (user) {
      redirectBasedOnRole(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setAuthError("");

    const cleanedEmail = email.trim().toLowerCase();
    if (!cleanedEmail || !password) {
      setAuthError("Invalid credentials");
      return;
    }

    setLoading(true);
    try {
      const loggedInUser = await login(cleanedEmail, password);
      toast.success("Welcome back!");
      redirectBasedOnRole(loggedInUser || user);
    } catch (err) {
      setAuthError("Invalid credentials");
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 font-sans selection:bg-blue-200 flex flex-col justify-between relative overflow-x-hidden">
      <div className="absolute top-10 left-10 w-96 h-96 bg-blue-500/15 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-500/15 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="relative w-full bg-gradient-to-r from-slate-900 via-indigo-900 to-blue-900 text-white pt-8 pb-32 px-6 rounded-b-[3.5rem] shadow-2xl overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]"></div>
        <div className="absolute -top-24 -right-24 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-5xl mx-auto flex items-center justify-between mb-8 relative z-10">
          <div className="[&_*]:!text-white [&_*]:!fill-white [&_*]:!border-white/30 brightness-125">
            <BrandHeader size="lg" />
          </div>
        </div>

        <div className="text-center max-w-xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest mb-3 backdrop-blur-md shadow-sm">
          
            <span>Welcome Back</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-none drop-shadow-md">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-indigo-200 to-white">
              Capture. Connect.<br />Convert.
            </span>
          </h1>
          <p className="text-blue-100/80 mt-2.5 text-sm sm:text-base font-medium max-w-md mx-auto">
            Access your unified dashboard and manage your trade show leads.
          </p>
        </div>
      </div>

      <main className="max-w-md w-full mx-auto -mt-20 px-4 relative z-20 pb-12 flex-grow">
        <div className="bg-white/90 backdrop-blur-2xl border border-white shadow-2xl shadow-indigo-950/10 rounded-[2.5rem] p-6 sm:p-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-900"></div>

          <div className="grid grid-cols-2 p-1.5 bg-slate-100/80 rounded-2xl mb-6 border border-slate-200/60 shadow-inner">
            <div className="py-2.5 text-center text-xs font-black bg-white text-blue-600 rounded-xl shadow-md border border-gray-100/80 cursor-default">
              LOGIN
            </div>
            <Link 
              to={`/auth/register${redirectUrl ? `?redirect=${encodeURIComponent(redirectUrl)}` : ''}`} 
              className="py-2.5 text-center text-xs font-bold text-gray-500 rounded-xl hover:text-gray-900 transition-all block"
            >
              SIGN UP
            </Link>
          </div>

          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {/* Email Field */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-700 ml-1">
                Email
              </label>
              <div className="relative">
                <div className={`absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none transition-colors ${
                  authError ? "text-rose-500" : "text-gray-400"
                }`}>
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  maxLength={254}
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (authError) setAuthError("");
                  }}
                  placeholder="you@company.com"
                  className={`w-full pr-4 py-3.5 rounded-2xl text-sm font-semibold transition-all outline-none ${
                    authError
                      ? "bg-rose-50/40 border border-rose-400 text-rose-950 placeholder-rose-300 focus:ring-4 focus:ring-rose-500/10 focus:border-rose-500"
                      : "bg-slate-50/70 border border-slate-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10"
                  }`}
                  style={{ paddingLeft: '2.75rem' }}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-700 ml-1">
                Password
              </label>
              <div className="relative">
                <div className={`absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none transition-colors ${
                  authError ? "text-rose-500" : "text-gray-400"
                }`}>
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  maxLength={128}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (authError) setAuthError("");
                  }}
                  placeholder="••••••••"
                  className={`w-full py-3.5 rounded-2xl text-sm font-semibold transition-all outline-none ${
                    authError
                      ? "bg-rose-50/40 border border-rose-400 text-rose-950 placeholder-rose-300 focus:ring-4 focus:ring-rose-500/10 focus:border-rose-500"
                      : "bg-slate-50/70 border border-slate-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10"
                  }`}
                  style={{ paddingLeft: '2.75rem', paddingRight: '3rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute p-2 transition-colors -translate-y-1/2 right-2.5 top-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {authError && (
                <div className="flex items-center gap-1.5 px-1 mt-1 text-xs font-semibold text-rose-600 animate-in fade-in slide-in-from-top-1 duration-200">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{authError}</span>
                </div>
              )}

              <div className="flex items-center justify-between mt-2 px-1">
                <button
                  type="button"
                  onClick={() => setIsOtpModalOpen(true)}
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                >
                  Try another way
                </button>

                <Link
                  to="/forgot-password"
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="h-14 w-full bg-slate-900 hover:bg-slate-800 text-white text-base font-black rounded-2xl flex items-center justify-center gap-2 shadow-xl shadow-slate-950/20 hover:scale-[1.01] active:scale-[0.98] transition-all duration-200 disabled:opacity-60 mt-2 group cursor-pointer"
            >
              {loading ? (
                "Authenticating…"
              ) : (
                <>
                  <span>Capture. Connect. Convert</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </div>
      </main>

      <OtpLoginModal
        isOpen={isOtpModalOpen}
        onClose={() => setIsOtpModalOpen(false)}
        initialEmail={email}
        onSuccess={(authUserData) => redirectBasedOnRole(authUserData || user)}
      />
    </div>
  );
}